import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { TackLogo } from "@/components/TackLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { getUserHomeRoute } from "@/lib/staff.functions";
import { supabase } from "@/integrations/supabase/client";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Tack. Logga in" },
      { name: "description", content: "Logga in på ditt Tack-konto och se feedback, belöningar och återbesök." },
      { property: "og:title", content: "Tack. Logga in" },
      { property: "og:description", content: "Logga in på ditt Tack-konto och se feedback, belöningar och återbesök." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://tack.company/login" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://tack.company/login" }],
  }),
  component: LoginPage,
});

function LoginPage() {
  const { login, resetPassword } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const reason = sessionStorage.getItem('auth_redirect_reason');
      if (reason === 'signin_required') {
        sessionStorage.removeItem('auth_redirect_reason');
        toast.info(t('auth.signinRequired'));
      }
    } catch {}
  }, [t]);

  const [unverified, setUnverified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const signedInSession = await login(email, password);
      setUnverified(false);
      const accessToken =
        signedInSession?.access_token ??
        (await supabase.auth.getSession()).data.session?.access_token ??
        null;

      try {
        const { route } = accessToken
          ? await getUserHomeRoute({ headers: { Authorization: `Bearer ${accessToken}` } })
          : { route: "/dashboard" as const };
        const allowed = ["/dashboard", "/cashier", "/select", "/onboarding"];
        const target = allowed.includes(route) ? route : "/dashboard";
        navigate({ to: target as any });
      } catch {
        navigate({ to: "/dashboard" });
      }
    } catch (err: any) {
      const msg: string = err?.message || "";
      if (/email not confirmed/i.test(msg)) {
        setUnverified(true);
      } else {
        toast.error(msg || t('auth.loginFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error(t('auth.enterEmailFirst'));
      return;
    }
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo:
            typeof window !== "undefined" ? `${window.location.origin}/auth/callback` : undefined,
        },
      });
      if (error) throw error;
      toast.success(t('auth.resendSent'));
      setResendCooldown(30);
    } catch (err: any) {
      toast.error(err.message || t('auth.resetSendFailed'));
    } finally {
      setResending(false);
    }
  };

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('auth.enterEmailFirst'));
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email);
      toast.success(t('auth.resetLinkSent'));
      setShowForgot(false);
    } catch (err: any) {
      toast.error(err.message || t('auth.resetSendFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <TackLogo size="lg" />
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="font-display">{showForgot ? t('auth.resetPassword') : t('auth.welcomeBack')}</CardTitle>
              <CardDescription>
                {showForgot ? t('auth.resetDesc') : t('auth.signInDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {showForgot ? (
                <form onSubmit={handleForgot} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('auth.email')}</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? t('auth.sending') : t('auth.sendResetLink')}
                  </Button>
                  <button type="button" onClick={() => setShowForgot(false)} className="w-full text-sm text-muted-foreground hover:text-foreground">
                    {t('auth.backToLogin')}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  {unverified && (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-3 space-y-2">
                      <p className="text-xs text-foreground">{t('auth.emailNotConfirmed')}</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={resendCooldown > 0 || resending}
                        onClick={handleResendVerification}
                      >
                        {resendCooldown > 0
                          ? t('auth.resendCooldown', { seconds: resendCooldown })
                          : resending
                          ? t('auth.sending')
                          : t('auth.resendVerification')}
                      </Button>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('auth.email')}</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? t('auth.signingIn') : t('auth.signIn')}
                  </Button>
                  <div className="flex items-center justify-between text-sm">
                    <button type="button" onClick={() => setShowForgot(true)} className="text-muted-foreground hover:text-foreground">
                      {t('auth.forgotPassword')}
                    </button>
                    <Link to="/signup" className="text-primary hover:underline">
                      {t('auth.createAccount')}
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <SiteFooter variant="legal" />
    </div>
  );
}
